// @flow
import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import Floater from "react-floater";
import styled, { css, keyframes, withTheme } from "styled-components";
import Media from "react-media";
import { Redirect } from "react-router-dom";

import { fontSize, rtl } from "../common/styled-mixins";
import Gettext from "../common/Gettext";
import Orientation, { isLandscape } from "../common/Orientation";
import { gaReducer } from "../GA";
import timer from "../../images/timer.svg";
import SideArrow from "../Icons/SideArrow";
import Close from "../Icons/Close";
import { get } from "../common/fetch";
import { useGenerativeData } from "./useGenerativeData";

export const TourContext = React.createContext(null);
const SightContext = React.createContext(false);

const Step = styled.div`
  direction: ${({ theme }) => theme.direction};
  display: flex;
  justify-content: space-between;
  padding-top: 5px;
  font-size: ${fontSize(12, 16)};
  ${rtl("text-align", "left")};
`;

const DesktopSkip = styled.div`
  font-size: ${fontSize(12, 16)};
  color: #0085eb;
  cursor: pointer;
  pointer-events: auto;
`;

const FooterContainer = styled.div`
  direction: ${({ theme }) => theme.direction};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 50px;
  ${rtl("padding-right", "15px")};
  background-color: #fff;
  border-radius: 0 0 8px 8px;
`;

const NextButton = styled.button`
  display: flex;
  align-items: center;
  font-size: ${fontSize(12)};
  font-weight: bold;
  color: #919191;
  text-transform: uppercase;
  background: transparent;
  border: none;
  cursor: pointer;
`;

const Timer = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(19px);
  height: 24px;
  padding: 0 15px;
  font-size: 14px;
  line-height: 24px;
  text-align: center;
  color: ${({ theme }) => theme.lightAccent};
  background-color: ${({ theme }) => theme.main};
  white-space: nowrap;
  border-radius: 8px 8px 0 0;

  ${({ isMobile }) =>
    isMobile &&
    css`
      top: auto;
      bottom: 100%;
      transform: translateX(-50%) translateY(-19px);
      border-radius: 0 0 8px 8px;
    `};

  ${({ isLandscape }) =>
    isLandscape &&
    css`
      top: auto;
      bottom: 100%;
      transform: translateX(-50%);
      border-radius: 8px 8px 0 0;
    `};
`;

const ButtonText = styled.span`
  color: ${({ theme }) => theme.lightAccent};
`;

const ArrowContainer = styled.div`
  ${rtl("margin-left", "12px")};
  opacity: 0.59;
`;

const Footer = ({ onNext }) => {
  return (
    <FooterContainer>
      <NextButton onClick={onNext}>
        <ButtonText>
          <Gettext>next step</Gettext>
        </ButtonText>
        <ArrowContainer>
          <SideArrow />
        </ArrowContainer>
      </NextButton>
    </FooterContainer>
  );
};

const MobileContainer = styled.div`
  direction: ${({ theme }) => theme.direction};
  display: flex;
  width: 100%;
  background-color: rgb(255, 211, 0);
`;

const MobileSide = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 57px;
  min-width: 57px;
  background-color: #fff;
`;

const MobileButtonContainer = styled(MobileSide)`
  align-self: stretch;

  > svg {
    opacity: 0.59;
  }
`;

const MobileContent = styled.div`
  width: 100%;
  padding: 15px;
  -webkit-text-size-adjust: 100%;

  div {
    margin-bottom: 0 !important;
    font-weight: bold !important;
    line-height: 16px !important;
  }
`;

const MobileArrow = styled.div`
  position: absolute;
  top: -14px;
  left: 65%;
  color: rgb(255, 211, 0);

  ${({ step, isLandscape }) =>
    step === "chartWithDeals" &&
    !isLandscape &&
    css`
      left: 50%;
      margin-left: -13px;
    `};

  ${({ step, isLandscape }) =>
    step === "chartWithDeals" &&
    isLandscape &&
    css`
      top: 100%;
      left: auto;
      right: 15px;
      margin-top: -3px;
      color: #fff;
      transform: rotate(180deg);
    `};

  ${({ step, isLandscape }) =>
    step === "chartWithProfit" &&
    isLandscape &&
    css`
      top: 5px;
      left: 100%;
      right: 15px;
      margin-right: -3px;
      color: #fff;
      transform: rotate(90deg);
    `};
`;

const Skip = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 3px;
  font-size: ${fontSize(14)};
  text-align: center;
  color: #000;
  opacity: 0.59;
  pointer-events: auto;
`;

const MobileTooltip = props => {
  const {
    withNextButton,
    tour,
    content,
    withMobileArrow,
    disabledTooltip,
    withTimer,
    isLandscape,
    rect,
    onLater
  } = props;
  const withArrow =
    withMobileArrow &&
    (tour.step === "chartWithProfit" ||
      tour.step === "dealsProfit" ||
      tour.step === "chartWithDeals");
  const disabled =
    disabledTooltip &&
    (tour.step === "chartWithProfit" || tour.step === "chartWithDeals");

  const tooltipStyles = () => {
    if (tour.step === "chartWithProfit") {
      return isLandscape
        ? {
            position: "absolute",
            width: rect.left - 25,
            top: 0,
            left: 0,
            zIndex: 10
          }
        : {
            position: "absolute",
            width: "100%",
            top: 67,
            zIndex: 10
          };
    }

    if (tour.step === "chartWithDeals") {
      return isLandscape
        ? {
            position: "absolute",
            width: "100%",
            top: 0,
            zIndex: 10
          }
        : {
            position: "absolute",
            width: "100%",
            top: rect.bottom + 20,
            zIndex: 10
          };
    }

    const bottom =
      tour.step === "allStocksSelect" ||
      (tour.step === "showChart" && !isLandscape) ||
      tour.step === "showStockSelect" ||
      tour.step === "chartWithDeals"
        ? "0"
        : "auto";
    const top =
      bottom === "auto"
        ? withArrow
          ? 67 + rect.scrollTop
          : rect.scrollTop
        : "auto";

    return {
      position: "absolute",
      width: "100%",
      top,
      bottom,
      zIndex: 10
    };
  };

  if (disabled) {
    return <React.Fragment>{props.children}</React.Fragment>;
  }

  return (
    <React.Fragment>
      {props.children}
      <div style={tooltipStyles()}>
        {withArrow && (
          <MobileArrow step={tour.step} isLandscape={isLandscape}>
            <svg
              width="26"
              height="14"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
            >
              <polygon fill="currentColor" points="26,14 13,0 0,14" />
            </svg>
          </MobileArrow>
        )}
        <MobileContainer>
          <MobileSide>
            <MobileButtonContainer>
              <Skip
                onClick={() => {
                  onLater("skip");
                }}
              >
                {tour.locale === "ph" ? (
                  <Close width={18} height={18} />
                ) : (
                  <Gettext>Skip tour</Gettext>
                )}
              </Skip>
            </MobileButtonContainer>
          </MobileSide>
          <MobileContent>{content}</MobileContent>
          {withNextButton && (
            <MobileSide>
              <MobileButtonContainer onClick={tour.next}>
                <SideArrow />
              </MobileButtonContainer>
            </MobileSide>
          )}
          {withTimer && (
            <MobileSide>
              <img src={timer} alt="" width={36} />
            </MobileSide>
          )}
        </MobileContainer>
      </div>
    </React.Fragment>
  );
};

const placement = (step: string, isMobile: boolean, isLandscape: boolean) => {
  switch (step) {
    case "selectAmount":
    case "selectTime":
    case "makeDeal":
      return "left";
    case "showChart":
      return "top-center";
    case "chartWithDeals":
      return "left";
    case "stockSelect":
      return "bottom-end";
    default:
      return "auto";
  }
};

type Props = {
  step: string | Array<string>,
  disabled?: boolean,
  originalRef: { current: any },
  children: ({
    next: () => void,
    step: string,
    isOriginal: boolean,
    isTourActive: boolean,
    onLater: () => void
  }) => React.Node,
  zIndex?: number,
  withMobileArrow?: boolean,
  disabledTooltip?: boolean,
  withoutStyles?: boolean,
  dealsContainer?: boolean,
  dealsTab?: boolean,
  showTimer?: boolean
};

function isCurrentStep(
  expected: string | Array<string>,
  current: string
): boolean {
  if (typeof expected === "string") {
    return expected === current;
  } else {
    return expected.includes(current);
  }
}

const Tour = (props: Props) => {
  let tour = useContext(TourContext);
  const hasActiveParentSight = useContext(SightContext);
  let [rect, setRect] = useState(null);
  let [showLater, onLater] = useState(false);

  function measure() {
    if (props.originalRef.current != null && tour != null) {
      let {
        top,
        right,
        bottom,
        left
      } = props.originalRef.current.getBoundingClientRect();
      let scrollTop = document.body.scrollTop;

      setRect(rect => {
        if (rect == null) {
          return { top, right, bottom, left, scrollTop: scrollTop };
        }
        if (
          rect.top === top &&
          rect.right === right &&
          rect.bottom === bottom &&
          rect.left === left &&
          rect.scrollTop === scrollTop
        ) {
          return rect;
        } else {
          return { top, right, bottom, left, scrollTop: scrollTop };
        }
      });
    }
  }

  useEffect(() => {
    let isRunning = true;

    function loop() {
      if (isRunning) {
        measure();
        requestAnimationFrame(loop);
      }
    }
    loop();

    return () => {
      isRunning = false;
    };
  }, []);

  /*useEffect(() => {
    // Кажется что на каждый апдейт мы реагируем новым апдейтом, иными словами —
    // мы в бесконечном цикле. Выживаем за счёт того что useEffect отложен по
    // таймеру.
    // Можно убедиться, если заменить useEffect на useLayoutEffect.
    // Туду пофиксить.
    if (props.originalRef.current != null && tour != null) {
      let {
        top,
        right,
        bottom,
        left
      } = props.originalRef.current.getBoundingClientRect();
      setRect({ top, right, bottom, left });
    }
  });*/

  if (showLater) {
    // В этой ветке есть две ошибки:
    // 1. Сайд-эффекты в рендере
    // 2. Ранний выход перед хуками
    // Пока живём, но надо будет на всякий случай поправить.
    try {
      if (!sessionStorage.getItem("showTourLater")) {
        sessionStorage.setItem("showTourLater", "true");
      }
    } catch (error) {
      console.log("sessionStorage error: ", error);
    }
    get(`/tours/stats?action=3&tour=tour${tour.analyticsID}`);
    if (showLater === "skip") {
      gaReducer(`CLICK_SKIP_TOUR_${tour.analyticsID}`, {
        step: tour.index - 1
      });
    } else {
      gaReducer(`SHOW_LATER_TOUR_${tour.analyticsID}`);
    }
    return <Redirect push to="/trade" />;
  }

  if (isCurrentStep(props.step, "intro")) {
    if (tour != null && tour.step === "intro") {
      return (
        <React.Fragment>
          {props.children({
            next: tour.next,
            step: tour.step,
            isOriginal: false,
            isTourActive: true,
            onLater: onLater
          })}
        </React.Fragment>
      );
    } else {
      return null;
    }
  }

  if (isCurrentStep(props.step, "outro")) {
    if (tour != null && tour.step === "outro") {
      return (
        <React.Fragment>
          {props.children({
            next: tour.next,
            step: tour.step,
            isOriginal: false,
            isTourActive: true,
            tour: tour
          })}
        </React.Fragment>
      );
    } else {
      return null;
    }
  }

  if (tour != null && isCurrentStep(props.step, tour.step) && rect != null) {
    const withFooter = tour.step === "showChart" || tour.step === "dealsProfit";
    const disabled = () => {
      return (
        props.disabled &&
        tour.step !== "stareAtTheChart" &&
        tour.step !== "showChart" &&
        tour.step !== "allStocksSelect"
      );
    };
    let padding =
      tour.step === "showChart" ||
      props.withoutStyles ||
      tour.analyticsID === "fillup"
        ? 0
        : 10;

    if (tour.analyticsID === "fillup") {
      return (
        <React.Fragment>
          {ReactDOM.createPortal(
            <Media query="(max-width: 959px)">
              {matches => (
                <Orientation>
                  {screen => {
                    return (
                      <Floater
                        open={true}
                        styles={{
                          wrapper: {
                            cursor: "default",
                            pointerEvents: "none",
                            zIndex: props.zIndex ? props.zIndex : 100
                          }
                        }}
                        content={tour.content}
                      >
                        <div
                          style={{
                            width: rect.right - rect.left + padding * 2,
                            height: rect.bottom - rect.top + padding * 2,
                            transform: `translateX(${rect.left -
                              padding}px) translateY(${rect.top - 14}px)`,
                            position: "absolute",
                            padding: padding,
                            background: "white",
                            pointerEvents: "auto",
                            zIndex: 150
                          }}
                        >
                          {props.children({
                            next: tour.next,
                            step: tour.step,
                            isOriginal: false,
                            isTourActive: true,
                            isTourMode: true,
                            deal: tour.deal,
                            startPointsLength: tour.startPointsLength,
                            balance: tour.balance,
                            tour: tour
                          })}
                        </div>
                      </Floater>
                    );
                  }}
                </Orientation>
              )}
            </Media>,
            tour.ref.current
          )}
          <SightContext.Provider value={true}>
            {props.children({
              next: () => void 0,
              step: tour.step,
              isOriginal: true,
              isTourActive: true,
              isTourMode: true,
              index: tour.index,
              of: tour.of,
              balance: tour.balance,
              tour: tour,
              startPointsLength: tour.startPointsLength
            })}
          </SightContext.Provider>
        </React.Fragment>
      );
    }

    if (hasActiveParentSight) {
      return (
        <React.Fragment>
          {props.children({
            next: () => void 0,
            step: tour.step,
            isOriginal: true,
            isTourActive: false,
            isTourMode: true,
            index: tour.index,
            of: tour.of,
            balance: tour.balance,
            tour: tour
          })}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        {ReactDOM.createPortal(
          <Media query="(max-width: 959px)">
            {matches => (
              <Orientation>
                {screen => {
                  return matches && tour.analyticsID === "3001" ? (
                    <MobileTooltip
                      isLandscape={isLandscape(screen)}
                      tour={tour}
                      content={tour.content}
                      withNextButton={withFooter}
                      withTimer={
                        tour.step === "chartWithProfit" ||
                        tour.step === "chartWithDeals"
                      }
                      withMobileArrow={props.withMobileArrow}
                      disabledTooltip={props.disabledTooltip}
                      rect={rect}
                      onLater={onLater}
                    >
                      <div
                        style={{
                          width: rect.right - rect.left + padding * 2,
                          height: rect.bottom - rect.top + padding * 2,
                          transform: `translateX(${rect.left -
                            padding}px) translateY(${
                            props.disabledTooltip &&
                            (tour.step === "chartWithProfit" &&
                              !isLandscape(screen))
                              ? rect.top - padding + 80 + rect.scrollTop
                              : tour.step === "chartWithDeals" &&
                                props.withoutStyles &&
                                isLandscape(screen)
                              ? rect.top - padding + 20 + rect.scrollTop
                              : rect.top - padding + rect.scrollTop
                          }px)`,
                          position: "absolute",
                          padding: padding,
                          background:
                            props.bgColor === "green"
                              ? props.theme.mainDark
                              : props.theme.main,
                          backgroundImage:
                            props.bgColor === "green" &&
                            `linear-gradient(to bottom, ${
                              props.theme.tradeHeaderGradientTop
                            } 0, ${
                              props.theme.tradeHeaderGradientBottom
                            } 100%)`,
                          borderRadius: props.dealsContainer
                            ? "6px 0 6px 6px"
                            : props.dealsTab
                            ? "6px 6px 0 0"
                            : props.withoutStyles
                            ? 0
                            : 6,
                          boxShadow:
                            props.withoutStyles &&
                            !props.dealsContainer &&
                            !props.dealsTab
                              ? "none"
                              : "0 0 20px rgba(0, 0, 0, 0.38)",
                          pointerEvents: disabled() ? "none" : "auto",
                          overflow:
                            props.dealsContainer || props.dealsTab
                              ? "hidden"
                              : "auto"
                        }}
                      >
                        {props.children({
                          next: tour.next,
                          step: tour.step,
                          isOriginal: false,
                          isTourActive: true,
                          isTourMode: true,
                          deal: tour.deal,
                          makeDeal: tour.makeDeal,
                          tourCurrentBid: tour.tourCurrentBid,
                          setTourCurrentBid: tour.setTourCurrentBid,
                          tourBalance: tour.tourBalance,
                          setTourBalance: tour.setTourBalance,
                          balance: tour.balance,
                          tour: tour,
                          startPointsLength: tour.startPointsLength
                        })}
                      </div>
                    </MobileTooltip>
                  ) : (
                    <Floater
                      footer={withFooter ? <Footer onNext={tour.next} /> : null}
                      styles={{
                        wrapper: {
                          cursor: "default",
                          pointerEvents: "none",
                          zIndex: props.zIndex ? props.zIndex : 100
                        },
                        floater: {
                          pointerEvents: "none",
                          maxWidth:
                            (matches &&
                              isLandscape(screen) &&
                              tour.step !== "makeDeal") ||
                            (!matches && tour.step === "stareAtTheChart")
                              ? 450
                              : 300
                        },
                        container: {
                          padding: 0,
                          backgroundColor: "rgb(255, 211, 0)",
                          borderRadius: 8,
                          cursor: "default"
                        },
                        arrow: {
                          length: 14,
                          spread: 26,
                          color:
                            tour.step === "showChart" && withFooter
                              ? "#fff"
                              : "rgb(255, 211, 0)"
                        }
                      }}
                      content={
                        <div
                          style={{
                            padding: 20,
                            paddingBottom: 5
                          }}
                        >
                          {tour.content}
                          <Step>
                            <DesktopSkip
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                onLater("skip");
                              }}
                            >
                              <Gettext>Skip tour</Gettext>
                            </DesktopSkip>
                            <div>
                              <Gettext>Step</Gettext>: {tour.index - 1}/
                              {tour.of - 1}
                            </div>
                          </Step>
                          {(matches && isLandscape(screen)) ||
                            (props.showTimer &&
                              tour.step === "stareAtTheChart" && (
                                <Timer isMobile={matches}>
                                  {/*prettier-ignore*/}
                                  <Gettext>
                                    {""}
                                    {`00:${tour.step === "stareAtTheChart" &&
                                      tour.deals &&
                                      tour.deals[0] &&
                                      tour.deals[0].bTime &&
                                      (tour.startPointsLength -
                                        tour.points.length +
                                        tour.deals[0].bTime <
                                      10
                                        ? `0${tour.startPointsLength -
                                            tour.points.length +
                                            tour.deals[0].bTime}`
                                        : tour.startPointsLength -
                                          tour.points.length +
                                          tour.deals[0].bTime)}`} until the closure
                                  </Gettext>
                                </Timer>
                              ))}
                        </div>
                      }
                      offset={
                        (matches &&
                          !isLandscape(screen) &&
                          props.showTimer &&
                          tour.step === "stareAtTheChart") ||
                        (props.showTimer && tour.step === "stareAtTheChart")
                          ? 29
                          : 5
                      }
                      open={!disabled()}
                      placement={placement(
                        tour.step,
                        matches,
                        isLandscape(screen)
                      )}
                    >
                      <div
                        style={{
                          width: rect.right - rect.left + padding * 2,
                          height: rect.bottom - rect.top + padding * 2,
                          transform: `translateX(${rect.left -
                            padding}px) translateY(${rect.top - 24}px)`,
                          position: "absolute",
                          padding:
                            (matches &&
                              !isLandscape(screen) &&
                              tour.analyticsID === "3000" &&
                              tour.step === "selectTime") ||
                            (matches &&
                              !isLandscape(screen) &&
                              tour.analyticsID === "3000" &&
                              tour.step === "selectAmount")
                              ? "10px 0"
                              : padding,
                          background:
                            props.bgColor === "green"
                              ? props.theme.mainDark
                              : props.theme.main,
                          backgroundImage:
                            props.bgColor === "green" &&
                            `linear-gradient(to bottom, ${
                              props.theme.tradeHeaderGradientTop
                            } 0, ${
                              props.theme.tradeHeaderGradientBottom
                            } 100%)`,
                          borderRadius: 6,
                          boxShadow: "0 0 20px rgba(0, 0, 0, 0.38)",
                          pointerEvents: disabled() ? "none" : "auto",
                          zIndex: 150
                        }}
                      >
                        {matches &&
                          isLandscape(screen) &&
                          (props.showTimer && tour.step === "stareAtTheChart" && (
                            <Timer isMobile={matches} isLandscape={true}>
                              {/*prettier-ignore*/}
                              <Gettext>
                                {""}
                                {`00:${tour.step === "stareAtTheChart" &&
                                tour.deals &&
                                tour.deals[0] &&
                                tour.deals[0].bTime &&
                                (tour.startPointsLength -
                                  tour.points.length +
                                  tour.deals[0].bTime <
                                10
                                  ? `0${tour.startPointsLength -
                                    tour.points.length +
                                    tour.deals[0].bTime}`
                                  : tour.startPointsLength -
                                    tour.points.length +
                                    tour.deals[0].bTime)}`} until the closure
                              </Gettext>
                            </Timer>
                          ))}
                        {props.children({
                          next: tour.next,
                          step: tour.step,
                          isOriginal: false,
                          isTourActive: true,
                          isTourMode: true,
                          deal: tour.deal,
                          startPointsLength: tour.startPointsLength,
                          balance: tour.balance,
                          tour: tour
                        })}
                      </div>
                    </Floater>
                  );
                }}
              </Orientation>
            )}
          </Media>,
          tour.ref.current
        )}
        <SightContext.Provider value={true}>
          {props.children({
            next: () => void 0,
            step: tour.step,
            isOriginal: true,
            isTourActive: true,
            isTourMode: true,
            index: tour.index,
            of: tour.of,
            balance: tour.balance,
            tour: tour,
            startPointsLength: tour.startPointsLength
          })}
        </SightContext.Provider>
      </React.Fragment>
    );
  } else if (tour != null) {
    return (
      <React.Fragment>
        {props.children({
          next: () => void 0,
          step: tour.step,
          isOriginal: true,
          isTourActive: false,
          isTourMode: true,
          index: tour.index,
          of: tour.of,
          balance: tour.balance,
          tour: tour
        })}
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        {props.children({
          next: () => void 0,
          isOriginal: true,
          isTourActive: false,
          isTourMode: false
        })}
      </React.Fragment>
    );
  }
};

export default withTheme(Tour);

function useGenerator(createInstance, ...initialArguments) {
  let gen = useRef(null);

  if (gen.current == null) {
    gen.current = createInstance(...initialArguments);
  }

  let [state, setState] = useState(() => {
    return gen.current.next().value;
  });

  function next(...args) {
    setState(gen.current.next(...args).value);
  }

  return [state, next];
}

type TourProviderProps = {
  analyticsID: string,
  scenario: Generator<*, *, *>,
  profile: *,
  apiConfigs: *,
  children: React.Node
};

export let TourProvider = (props: TourProviderProps) => {
  const { currency, locale } = props.profile;
  const bids = props.apiConfigs.currencySettings[currency].bids;
  const [outside, clickOutside] = useState(false);
  let [startPointsLength, getPointsLength] = useState(30);

  const borderStyles = {
    border: "border: 2px solid #0085EB;",
    keyframe: keyframes`
  50% {
    border-color: transparent;
  }
`,
    animation: () =>
      css`
        animation: ${borderStyles.keyframe} 0.4s linear;
        animation-iteration-count: 2;
      `
  };

  let dataRef = useRef(null);

  let [state, next] = useGenerator(props.scenario, dataRef, currency, bids);

  useGenerativeData(dataRef, {
    stock: state.stock,
    isRunning: state.isTimeRunning,
    deals: state.deals,
    next: next,
    attempt: state.attempt
  });

  let ref = useRef(null);

  useEffect(() => {
    get(`/tours/stats?action=1&tour=tour${props.analyticsID}`);
  }, []);

  useEffect(() => {
    clickOutside(false);
    if (state.index === 1) {
      gaReducer(`SHOW_TOUR_${props.analyticsID}`);
    } else {
      gaReducer(`SHOW_STEP_TOUR_${props.analyticsID}`, {
        step: state.index - 1
      });
    }
  }, [state.index]);

  useEffect(() => {
    let timer;
    if (outside) {
      timer = setTimeout(() => clickOutside(false), 1000);
    }

    return () => clearTimeout(timer);
  }, [outside]);

  useEffect(() => {
    if (
      state.step === "stareAtTheChart" &&
      state.deals &&
      state.deals[0] &&
      state.deals[0].bTime
    ) {
      getPointsLength(dataRef.current.slice().length);
    }
  }, [state.index]);

  let tour = useMemo(() => {
    return {
      analyticsID: props.analyticsID,
      step: state.step,
      next: next,
      ref: ref,
      index: state.index,
      of: state.of,
      balance: state.balance,
      deals: state.deals,
      content: state.content,
      points: dataRef.current,
      activeDeal: state.activeDeal,
      locale: locale,
      outside: outside,
      borderStyles: borderStyles,
      startPointsLength: startPointsLength
    };
  }, [state, next, props.analyticsID, dataRef.current]);

  let container = (
    <div
      onClick={() => clickOutside(true)}
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        background: "rgba(0, 0, 0, 0.25)",
        zIndex: 5
      }}
      ref={ref}
    />
  );

  return (
    <TourContext.Provider value={tour}>
      {props.children}
      {container}
    </TourContext.Provider>
  );
};



// WEBPACK FOOTER //
// ./src/components/Tour/index.js